import React, { useEffect, useMemo, useCallback, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Col, Row, UncontrolledTooltip, Modal, ModalHeader, ModalBody, Form,
  Input, FormFeedback, Label, Card, CardBody, Button, Pagination,
  PaginationItem, PaginationLink, ModalFooter, Badge
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableContainer from '../../components/Common/TableContainer';
import Spinners from "components/Common/Spinner";
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';
import {
  fetchVideos,
  createVideo,
  updateVideo,
  deleteVideo,
} from '../../services/videoService';
import { fetchJenisSoal } from '../../services/Master/MasterJenisSoal';
import { fetchJenisSeleksi } from '../../services/Master/MasterJenisSeleksi';
import { fetchTipeKonten } from '../../services/Master/MasterTipeKonten';
import { fetchKategori } from '../../services/Master/kategoriService';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;
const DEFAULT_FILTERS = {
  id_jenis_soal: "",
  id_jenis_seleksi: "",
  id_tipe_konten: "",
  id_kategori: "",
  search: "",
  id_video: ""
};

const initialState = {
  modal: false,
  isEdit: false,
  video: null,
  data: [],
  loading: true,
  loadingOptions: false,
  loadingJenisSoalModal: false,
  jenisSoalOptions: [],
  jenisSoalModalOptions: [],
  jenisSeleksiOptions: [],
  tipeKontenOptions: [],
  kategoriOptions: [],
  filteredJenisSeleksi: [],
  filteredJenisSeleksiModal: [],
  isOptionsLoaded: false,
  deleteModal: false,
  pagination: {
    page: DEFAULT_PAGE,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
    last_page: 1
  },
  filters: { ...DEFAULT_FILTERS }
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STATE':
      return { ...state, ...action.payload };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, page: DEFAULT_PAGE }
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: { ...DEFAULT_FILTERS },
        pagination: { ...state.pagination, page: DEFAULT_PAGE }
      };
    case 'FILTER_JENIS_SELEKSI':
      return {
        ...state,
        filteredJenisSeleksi: action.payload
      };
    case 'FILTER_JENIS_SELEKSI_MODAL':
      return {
        ...state,
        filteredJenisSeleksiModal: action.payload
      };
    case 'SET_JENIS_SOAL_MODAL_OPTIONS':
      return {
        ...state,
        jenisSoalModalOptions: action.payload
      };
    default:
      return state;
  }
}

const VideoList = () => {
  document.title = "Daftar Video";
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idVideo = queryParams.get('id_video');

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    filters: { ...initialState.filters, id_video: idVideo || "" }
  });

  const {
    modal, isEdit, video, data, loading,
    jenisSoalOptions, tipeKontenOptions, kategoriOptions,
    filteredJenisSeleksi, filteredJenisSeleksiModal,
    jenisSoalModalOptions, loadingJenisSoalModal,
    isOptionsLoaded, deleteModal, pagination, filters, loadingOptions
  } = state;

  const tipeOptions = [
    { value: "Belajar", label: "Belajar" },
    { value: "Rekaman", label: "Rekaman" },
  ];

  const filterJenisSeleksi = useCallback(async (idTipeKonten) => {
    try {
      if (!idTipeKonten) {
        dispatch({ type: 'FILTER_JENIS_SELEKSI', payload: [] });
        return;
      }

      const response = await fetchJenisSeleksi({
        id_tipe_konten: idTipeKonten,
        disable_pagination: true
      });

      const filtered = response.data || [];

      dispatch({ type: 'FILTER_JENIS_SELEKSI', payload: filtered });

      if (filters.id_jenis_seleksi && !filtered.some(s => s.id_jenis_seleksi === parseInt(filters.id_jenis_seleksi))) {
        dispatch({
          type: 'SET_FILTERS',
          payload: { id_jenis_seleksi: "" }
        });
      }
      if (filters.id_jenis_soal && !jenisSoalOptions.some(s => s.id_jenis_soal === parseInt(filters.id_jenis_soal))) {
        dispatch({
          type: 'SET_FILTERS',
          payload: { id_jenis_soal: "" }
        });
      }
    } catch (error) {
      console.error("Error filtering jenis seleksi (table):", error);
      toast.error(error.message || "Gagal memuat jenis seleksi (filter). Silakan coba lagi.");
    }
  }, [filters.id_jenis_seleksi, filters.id_jenis_soal, jenisSoalOptions]);

  const filterJenisSeleksiForModal = useCallback(async (idTipeKonten) => {
    try {
      if (!idTipeKonten) {
        dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: [] });
        return;
      }
      const response = await fetchJenisSeleksi({
        id_tipe_konten: idTipeKonten,
        disable_pagination: true
      });
      dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: response.data || [] });
    } catch (error) {
      console.error("Error filtering jenis seleksi (modal):", error);
      toast.error(error.message || "Gagal memuat jenis seleksi (modal). Silakan coba lagi.");
    }
  }, []);

  const fetchJenisSoalForModal = useCallback(async (idTipeKonten, idJenisSeleksi) => {
    if (!idTipeKonten || !idJenisSeleksi) {
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
      return;
    }

    dispatch({ type: 'SET_STATE', payload: { loadingJenisSoalModal: true } });
    try {
      const response = await fetchJenisSoal({
        id_tipe_konten: idTipeKonten,
        id_jenis_seleksi: idJenisSeleksi,
        disable_pagination: true
      });

      if (response.success && response.data) {
        dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: response.data });
      } else {
        dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
        console.error("Failed to fetch jenis soal for modal:", response.message);
      }
    } catch (error) {
      console.error("Error fetching jenis soal for modal:", error);
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
    } finally {
      dispatch({ type: 'SET_STATE', payload: { loadingJenisSoalModal: false } });
    }
  }, []);

  useEffect(() => {
    if (isOptionsLoaded && filters.id_tipe_konten) {
      filterJenisSeleksi(filters.id_tipe_konten);
    }
  }, [filters.id_tipe_konten, isOptionsLoaded, filterJenisSeleksi]);

  const getVideoIdFromEmbed = (embedCode) => {
    if (!embedCode) return null;
    const regex = /src="https:\/\/www\.youtube\.com\/embed\/([^"?]+)/;
    const match = embedCode.match(regex);
    return match ? match[1] : null;
  };

  const validationSchema = Yup.object({
    id_tipe_konten: Yup.number().required("Tipe Konten wajib diisi"),
    id_jenis_soal: Yup.number().required("Jenis Soal wajib diisi"),
    id_jenis_seleksi: Yup.number().required("Jenis Seleksi wajib diisi"),
    id_kategori: Yup.number().required("Kategori wajib diisi"),
    judul: Yup.string().required("Judul video wajib diisi"),
    tipe: Yup.string().required("Tipe wajib diisi"),
    thumbnail_url: Yup.string()
      .required("Embed code wajib diisi")
      .test(
        'is-valid-embed',
        'Embed code tidak valid',
        value => value.includes('<iframe') && value.includes('youtube.com/embed')
      ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_tipe_konten: video?.id_tipe_konten || "",
      id_jenis_soal: video?.id_jenis_soal || "",
      id_jenis_seleksi: video?.id_jenis_seleksi || "",
      id_kategori: video?.id_kategori || "",
      judul: video?.judul || "",
      thumbnail_url: video?.thumbnail_url || "",
      deskripsi: video?.deskripsi || "",
      tipe: video?.tipe || "",
      thumbnail_image: null
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        const formData = new FormData();

        Object.keys(values).forEach(key => {
          if (key === 'thumbnail_image') {
            if (values[key] instanceof File) {
              formData.append(key, values[key]);
            }
            else if (isEdit && values[key] === null && video?.thumbnail_image) {
              formData.append('remove_thumbnail', '1');
            }
          } else {
            formData.append(key, values[key] !== null ? values[key] : '');
          }
        });

        const response = isEdit
          ? await updateVideo(video.id_video, formData)
          : await createVideo(formData);

        if (response.success) {
          toast.success(response.message || (isEdit ? "Video berhasil diperbarui" : "Video berhasil ditambahkan"));
          fetchData();
          toggleModal();
          if (idVideo && !isEdit) {
            navigate(location.pathname, { replace: true });
          }
        } else {
          throw new Error(response.message || "Gagal menyimpan video.");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error(error.message || "Terjadi kesalahan saat menyimpan.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchData = useCallback(async () => {
    if (!isOptionsLoaded) return;

    try {
      dispatch({ type: 'SET_STATE', payload: { loading: true } });

      const response = await fetchVideos(
        pagination.page,
        pagination.per_page,
        filters
      );

      if (response && response.success) {
        const calculatedLastPage = Math.ceil(response.meta.total / response.meta.per_page);
        const videoData = (response.data || []).map(videoItem => ({
          ...videoItem,
          jenis_seleksi_nama: videoItem.jenis_seleksi_nama || '-',
          jenis_soal_nama: videoItem.jenis_soal_nama || '-',
          tipe_konten_nama: videoItem.tipe_konten_nama || '-',
          kategori_nama: videoItem.kategori_nama || '-'
        }));

        dispatch({
          type: 'SET_STATE',
          payload: {
            data: videoData,
            pagination: {
              ...pagination,
              total: response.meta.total || 0,
              last_page: calculatedLastPage || 1
            },
            loading: false
          }
        });
      } else {
        throw new Error(response?.message || "Gagal memuat data video.");
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
      dispatch({ 
        type: 'SET_STATE', 
        payload: { 
          data: [],
          loading: false,
          pagination: {
            ...pagination,
            total: 0,
            last_page: 1
          }
        } 
      });
      toast.error(error.message || "Terjadi kesalahan saat memuat data video.");
    }
  }, [isOptionsLoaded, pagination.page, pagination.per_page, filters]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        dispatch({ type: 'SET_STATE', payload: { loadingOptions: true } });

        const [jenisSoalRes, tipeKontenRes, kategoriRes] = await Promise.all([
          fetchJenisSoal(1, 100, {}, true),
          fetchTipeKonten(1, 100, {}, true),
          fetchKategori(1, 100, {}, true)
        ]);

        dispatch({
          type: 'SET_STATE',
          payload: {
            jenisSoalOptions: jenisSoalRes.data || [],
            tipeKontenOptions: tipeKontenRes.data || [],
            kategoriOptions: kategoriRes.data || [],
            isOptionsLoaded: true,
            loadingOptions: false
          }
        });
      } catch (error) {
        console.error("Error loading options:", error);
        toast.error("Gagal memuat opsi pilihan. Silakan coba lagi.");
        dispatch({ type: 'SET_STATE', payload: { loadingOptions: false } });
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    if (isOptionsLoaded) {
      fetchData();
    }
  }, [isOptionsLoaded, fetchData]);

  useEffect(() => {
    if (idVideo && isOptionsLoaded) {
      const fetchSingleVideo = async (id) => {
        try {
          dispatch({ type: 'SET_STATE', payload: { loading: true } });
          const response = await fetchVideos(1, 1, { id_video: id });

          if (response.success && response.data.length > 0) {
            const fetchedVideo = response.data[0];
            dispatch({
              type: 'SET_STATE',
              payload: {
                video: fetchedVideo,
                isEdit: true,
                modal: true,
                loading: false
              }
            });
            formik.setValues({
              id_tipe_konten: fetchedVideo.id_tipe_konten || "",
              id_jenis_seleksi: fetchedVideo.id_jenis_seleksi || "",
              id_jenis_soal: fetchedVideo.id_jenis_soal || "",
              id_kategori: fetchedVideo.id_kategori || "",
              judul: fetchedVideo.judul || "",
              thumbnail_url: fetchedVideo.thumbnail_url || "",
              deskripsi: fetchedVideo.deskripsi || "",
              tipe: fetchedVideo.tipe || "",
              thumbnail_image: null
            });
          } else {
            throw new Error("Video tidak ditemukan.");
          }
        } catch (error) {
          console.error("Error fetching single video:", error);
          toast.error(error.message || "Gagal memuat data video.");
          dispatch({ type: 'SET_STATE', payload: { loading: false } });
          navigate(-1);
        }
      };

      fetchSingleVideo(idVideo);
    }
  }, [idVideo, isOptionsLoaded, navigate]);

  useEffect(() => {
    if (modal && formik.values.id_tipe_konten) {
      filterJenisSeleksiForModal(formik.values.id_tipe_konten);
    } else if (modal && !formik.values.id_tipe_konten) {
      dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: [] });
    }
  }, [modal, formik.values.id_tipe_konten, filterJenisSeleksiForModal]);

  useEffect(() => {
    if (modal && formik.values.id_tipe_konten && formik.values.id_jenis_seleksi) {
      fetchJenisSoalForModal(formik.values.id_tipe_konten, formik.values.id_jenis_seleksi);
    } else if (modal) {
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
    }
  }, [modal, formik.values.id_tipe_konten, formik.values.id_jenis_seleksi, fetchJenisSoalForModal]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > pagination.last_page) return;

    dispatch({
      type: 'SET_PAGINATION',
      payload: { page: newPage }
    });
  }, [pagination.last_page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'id_tipe_konten') {
      dispatch({
        type: 'SET_FILTERS',
        payload: {
          [name]: value,
          id_jenis_seleksi: "",
          id_jenis_soal: ""
        }
      });
    } else if (name === 'id_jenis_seleksi') {
      dispatch({
        type: 'SET_FILTERS',
        payload: {
          [name]: value,
          id_jenis_soal: ""
        }
      });
    } else {
      dispatch({
        type: 'SET_FILTERS',
        payload: { [name]: value }
      });
    }
  };

  const clearFilters = () => {
    dispatch({ type: 'RESET_FILTERS' });
  };

  const toggleModal = () => {
    if (modal) {
      dispatch({
        type: 'SET_STATE',
        payload: {
          modal: false,
          video: null,
          isEdit: false,
          jenisSoalModalOptions: [],
          filteredJenisSeleksiModal: [],
        }
      });
      formik.resetForm();
      if (idVideo) {
        navigate(location.pathname, { replace: true });
      }
    } else {
      dispatch({ type: 'SET_STATE', payload: { modal: true, isEdit: false, video: null } });
      formik.resetForm();
    }
  };

  const handleVideoClick = useCallback((videoData) => {
    dispatch({
      type: 'SET_STATE',
      payload: {
        video: videoData,
        isEdit: true,
        modal: true
      }
    });
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'SET_STATE', payload: { deleteModal: false, video: null } });
  }, []);

  const handleConfirmDelete = async () => {
    try {
      if (!video?.id_video) return;

      const response = await deleteVideo(video.id_video);
      if (response.success) {
        toast.success("Video berhasil dihapus.");
        const newPage = data.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
        dispatch({
          type: 'SET_PAGINATION',
          payload: { page: newPage }
        });
        handleCloseDeleteModal();
      } else {
        throw new Error(response.message || "Gagal menghapus video.");
      }
    } catch (error) {
      console.error("Error deleting video:", error);
      toast.error(error.message || "Gagal menghapus video.");
    }
  };

  const onClickDelete = useCallback((videoData) => {
    dispatch({
      type: 'SET_STATE',
      payload: {
        video: videoData,
        deleteModal: true
      }
    });
  }, []);

  const renderImagePreview = useCallback(() => {
    const fileInput = formik.values.thumbnail_image;
    const existingImageUrl = video?.thumbnail_image;

    if (fileInput instanceof File) {
      return (
        <div className="mt-2">
          <img
            src={URL.createObjectURL(fileInput)}
            alt="Preview thumbnail baru"
            className="img-thumbnail"
            style={{ maxWidth: '100px', maxHeight: '100px' }}
          />
          <small className="d-block text-muted">Preview Thumbnail Baru</small>
          <Button
            size="sm"
            color="light"
            className="mt-1"
            onClick={() => formik.setFieldValue("thumbnail_image", null)}
          >
            Hapus
          </Button>
        </div>
      );
    } else if (isEdit && existingImageUrl) {
      return (
        <div className="mt-2">
          <img
            src={existingImageUrl}
            alt="Thumbnail saat ini"
            className="img-thumbnail"
            style={{ maxWidth: '100px', maxHeight: '100px' }}
          />
          <small className="d-block text-muted">Thumbnail Saat Ini</small>
          <Button
            size="sm"
            color="light"
            className="mt-1"
            onClick={() => formik.setFieldValue("thumbnail_image", null)}
          >
            Hapus Thumbnail
          </Button>
        </div>
      );
    }
    return null;
  }, [formik.values.thumbnail_image, isEdit, video]);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  const columns = useMemo(() => [
    {
      header: 'No',
      accessorKey: "id_video",
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="text-muted">
          {((pagination.page - 1) * pagination.per_page) + cell.row.index + 1}
        </span>
      )
    },
    {
      header: 'Tipe Konten',
      accessorKey: "tipe_konten",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="primary" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Jenis Seleksi',
      accessorKey: "jenis_seleksi_nama",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="info" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Jenis Soal',
      accessorKey: "jenis_soal",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="success" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Kategori',
      accessorKey: "kategori",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="secondary" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Tipe',
      accessorKey: "tipe",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color={cell.getValue() === "Belajar" ? "warning" : "dark"} className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Judul Video',
      accessorKey: "judul",
      enableColumnFilter: false,
      cell: (cell) => (
        <div className="text-truncate" style={{ maxWidth: '200px' }}>
          {cell.getValue() || '-'}
        </div>
      )
    },
    {
      header: 'Dibuat',
      accessorKey: "created_at",
      enableColumnFilter: false,
      cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          
          return `${year}-${month}-${day}`;
      }
    },
    {
      header: 'Aksi',
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (cellProps) => {
        if (!cellProps.row.original) return null;

        return (
          <div className="hstack gap-2">
            <Button
              color="soft-info"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleVideoClick(cellProps.row.original);
              }}
              id={`edittooltip-${cellProps.row.original.id_video}`}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`edittooltip-${cellProps.row.original.id_video}`}
            >
              Edit
            </UncontrolledTooltip>

            <Button
              color="soft-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClickDelete(cellProps.row.original);
              }}
              id={`deletetooltip-${cellProps.row.original.id_video}`}
            >
              <i className="mdi mdi-delete-outline" />
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`deletetooltip-${cellProps.row.original.id_video}`}
            >
              Hapus
            </UncontrolledTooltip>
          </div>
        );
      }
    }
  ], [pagination.page, pagination.per_page, handleVideoClick, onClickDelete]);

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={3000} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Video" breadcrumbItem="Daftar Video" />

          {loading && !isOptionsLoaded ? (
            <Spinners />
          ) : (
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody className="border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="mb-0 card-title">Daftar Video</h5>
                      </div>
                      <Button color="primary" onClick={toggleModal}>
                        <i className="mdi mdi-plus me-1"></i> Tambah Video
                      </Button>
                    </div>
                  </CardBody>

                  <CardBody>
                    <div className="mb-4">
                      <Row className="g-3">
                        <Col md={2}>
                          <Label htmlFor="id_tipe_konten_filter">Tipe Konten</Label>
                          <Input
                            type="select"
                            name="id_tipe_konten"
                            id="id_tipe_konten_filter"
                            value={filters.id_tipe_konten}
                            onChange={handleFilterChange}
                            className="form-select"
                          >
                            <option value="">Semua Tipe Konten</option>
                            {tipeKontenOptions.map((tipe) => (
                              <option key={tipe.id_tipe_konten} value={tipe.id_tipe_konten}>
                                {tipe.tipe_konten}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={2}>
                          <Label htmlFor="id_jenis_seleksi_filter">Jenis Seleksi</Label>
                          <Input
                            type="select"
                            name="id_jenis_seleksi"
                            id="id_jenis_seleksi_filter"
                            value={filters.id_jenis_seleksi}
                            onChange={handleFilterChange}
                            className="form-select"
                            disabled={!filters.id_tipe_konten}
                          >
                            <option value="">Semua Seleksi</option>
                            {filteredJenisSeleksi.map((seleksi) => (
                              <option key={seleksi.id_jenis_seleksi} value={seleksi.id_jenis_seleksi}>
                                {seleksi.nama_seleksi}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={2}>
                          <Label htmlFor="id_jenis_soal_filter">Jenis Soal</Label>
                          <Input
                            type="select"
                            name="id_jenis_soal"
                            id="id_jenis_soal_filter"
                            value={filters.id_jenis_soal}
                            onChange={handleFilterChange}
                            className="form-select"
                            disabled={!filters.id_jenis_seleksi}
                          >
                            <option value="">Semua Jenis Soal</option>
                            {jenisSoalOptions
                              .filter(soal => filters.id_jenis_seleksi 
                                ? soal.id_jenis_seleksi === parseInt(filters.id_jenis_seleksi) 
                                : true)
                              .map((soal) => (
                                <option key={soal.id_jenis_soal} value={soal.id_jenis_soal}>
                                  {soal.nama_jenis}
                                </option>
                              ))}
                          </Input>
                        </Col>

                        <Col md={2}>
                          <Label htmlFor="id_kategori_filter">Kategori</Label>
                          <Input
                            type="select"
                            name="id_kategori"
                            id="id_kategori_filter"
                            value={filters.id_kategori}
                            onChange={handleFilterChange}
                            className="form-select"
                          >
                            <option value="">Semua Kategori</option>
                            {kategoriOptions.map((kategori) => (
                              <option key={kategori.id_kategori} value={kategori.id_kategori_paket}>
                                {kategori.kategori}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={2} className="d-flex align-items-end">
                          <Button
                            color="danger"
                            outline
                            onClick={clearFilters}
                            className="w-100"
                          >
                            <i className="mdi mdi-refresh me-1"></i> Reset
                          </Button>
                        </Col>
                      </Row>
                    </div>

                    <TableContainer
                      columns={columns}
                      data={data}
                      isGlobalFilter={false}
                      isPagination={false}
                      customPageSize={pagination.per_page}
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    />

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Menampilkan <span className="fw-semibold">{pagination.total > 0 ? ((pagination.page - 1) * pagination.per_page) + 1 : 0}</span> sampai{' '}
                        <span className="fw-semibold">{Math.min(pagination.page * pagination.per_page, pagination.total)}</span> dari{' '}
                        <span className="fw-semibold">{pagination.total}</span> data
                      </div>

                      <div className="d-flex align-items-center">
                        <Pagination className="pagination pagination-rounded mb-0">
                          <PaginationItem disabled={pagination.page === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(pagination.page - 1)} />
                          </PaginationItem>

                          {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                            let pageNum;
                            if (pagination.last_page <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.last_page - 2) {
                              pageNum = pagination.last_page - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }

                            return (
                              <PaginationItem key={pageNum} active={pageNum === pagination.page}>
                                <PaginationLink onClick={() => handlePageChange(pageNum)}>
                                  {pageNum}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          })}

                          <PaginationItem disabled={pagination.page === pagination.last_page}>
                            <PaginationLink next onClick={() => handlePageChange(pagination.page + 1)} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
            <ModalHeader toggle={toggleModal} tag="h4" className="bg-light p-3">
              {isEdit ? "Edit Video" : "Tambah Video"}
            </ModalHeader>

            <ModalBody className="px-4 pt-4">
              <Form onSubmit={formik.handleSubmit}>
                <Row className="g-4">
                  <Col md={12}>
                    <Card className="shadow-sm border">
                      <CardBody>
                        <h5 className="font-size-16 mb-3 border-bottom pb-2">Informasi Video</h5>

                        <div className="mb-3">
                          <Label htmlFor="id_tipe_konten" className="form-label">Tipe Konten <span className="text-danger">*</span></Label>
                          <Input
                            id="id_tipe_konten"
                            name="id_tipe_konten"
                            type="select"
                            onChange={async (e) => {
                              const idTipeKonten = e.target.value;
                              formik.setFieldValue("id_tipe_konten", idTipeKonten);
                              formik.setFieldValue("id_jenis_seleksi", "");
                              formik.setFieldValue("id_jenis_soal", "");
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_tipe_konten || ""}
                            invalid={!!(formik.touched.id_tipe_konten && formik.errors.id_tipe_konten)}
                            className="form-select"
                          >
                            <option value="">Pilih Tipe Konten</option>
                            {tipeKontenOptions.map((tipe) => (
                              <option key={tipe.id_tipe_konten} value={tipe.id_tipe_konten}>
                                {tipe.tipe_konten}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_tipe_konten}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="id_jenis_seleksi" className="form-label">Jenis Seleksi <span className="text-danger">*</span></Label>
                          <Input
                            id="id_jenis_seleksi"
                            name="id_jenis_seleksi"
                            type="select"
                            onChange={async (e) => {
                              const selectedJenisSeleksiId = e.target.value;
                              formik.setFieldValue("id_jenis_seleksi", selectedJenisSeleksiId);
                              formik.setFieldValue("id_jenis_soal", "");
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_jenis_seleksi || ""}
                            invalid={!!(formik.touched.id_jenis_seleksi && formik.errors.id_jenis_seleksi)}
                            className="form-select"
                            disabled={!formik.values.id_tipe_konten || filteredJenisSeleksiModal.length === 0}
                          >
                            <option value="">
                                {!formik.values.id_tipe_konten ? "Pilih Tipe Konten dulu" :
                                 (filteredJenisSeleksiModal.length === 0 && formik.values.id_tipe_konten) ? "Tidak ada Jenis Seleksi" : "Pilih Jenis Seleksi"
                                }
                            </option>
                            {filteredJenisSeleksiModal.map((seleksi) => (
                              <option key={seleksi.id_jenis_seleksi} value={seleksi.id_jenis_seleksi}>
                                {seleksi.nama_seleksi}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_jenis_seleksi}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="id_jenis_soal" className="form-label">Jenis Soal <span className="text-danger">*</span></Label>
                          <Input
                            id="id_jenis_soal"
                            name="id_jenis_soal"
                            type="select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_jenis_soal || ""}
                            invalid={!!(formik.touched.id_jenis_soal && formik.errors.id_jenis_soal)}
                            className="form-select"
                            disabled={!formik.values.id_jenis_seleksi || loadingJenisSoalModal}
                          >
                            <option value="">
                              {loadingJenisSoalModal ? "Memuat..." : 
                               (!formik.values.id_jenis_seleksi ? "Pilih Jenis Seleksi dulu" :
                                (jenisSoalModalOptions.length === 0 ? "Tidak ada Jenis Soal" : "Pilih Jenis Soal"))}
                            </option>
                            {jenisSoalModalOptions.map((soal) => (
                              <option key={soal.id_jenis_soal} value={soal.id_jenis_soal}>
                                {soal.nama_jenis}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_jenis_soal}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="id_kategori" className="form-label">Kategori <span className="text-danger">*</span></Label>
                          <Input
                            id="id_kategori"
                            name="id_kategori"
                            type="select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.id_kategori || ""}
                            invalid={!!(formik.touched.id_kategori && formik.errors.id_kategori)}
                            className="form-select"
                          >
                            <option value="">Pilih Kategori</option>
                            {kategoriOptions.map((kategori) => (
                              <option key={kategori.id_kategori} value={kategori.id_kategori_paket}>
                                {kategori.kategori}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.id_kategori}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="tipe" className="form-label">
                              Tipe <span className="text-danger">*</span>
                          </Label>
                          <Input
                              id="tipe"
                              name="tipe"
                              type="select"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.tipe || ""}
                              invalid={!!(formik.touched.tipe && formik.errors.tipe)}
                              className="form-select"
                          >
                            <option value="">Pilih Tipe</option>
                            {tipeOptions.map((cat) => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>{formik.errors.tipe}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="judul" className="form-label">Judul Video <span className="text-danger">*</span></Label>
                          <Input
                            id="judul"
                            name="judul"
                            type="text"
                            placeholder="Masukkan judul video"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.judul || ""}
                            invalid={!!(formik.touched.judul && formik.errors.judul)}
                          />
                          <FormFeedback>{formik.errors.judul}</FormFeedback>
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="thumbnail_url" className="form-label">Embed Code Video <span className="text-danger">*</span></Label>
                          <Input
                            id="thumbnail_url"
                            name="thumbnail_url"
                            type="textarea"
                            rows={5}
                            placeholder="Masukkan embed code video (contoh: <iframe ...>)"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.thumbnail_url || ""}
                            invalid={!!(formik.touched.thumbnail_url && formik.errors.thumbnail_url)}
                          />
                          <FormFeedback>{formik.errors.thumbnail_url}</FormFeedback>
                          
                          {formik.values.thumbnail_url && !formik.errors.thumbnail_url && (
                            <div className="mt-3">
                              <Label>Preview Video</Label>
                              <div 
                                className="video-preview-container" 
                                dangerouslySetInnerHTML={{ __html: formik.values.thumbnail_url }}
                                style={{ maxWidth: '100%', overflow: 'hidden' }}
                              />
                              <small className="text-muted">Pastikan video dapat diputar dengan benar</small>
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="thumbnail_image" className="form-label">Thumbnail Video</Label>
                          <Input
                            id="thumbnail_image"
                            name="thumbnail_image"
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                              formik.setFieldValue(
                                "thumbnail_image", 
                                event.currentTarget.files[0] || null
                              );
                            }}
                            className="form-control"
                          />
                          {renderImagePreview()}
                        </div>

                        <div className="mb-0">
                          <Label htmlFor="deskripsi" className="form-label">Deskripsi Video (Opsional)</Label>
                          <ReactQuill
                            theme="snow"
                            value={formik.values.deskripsi || ''}
                            onChange={(content) => {
                              formik.setFieldValue("deskripsi", content);
                            }}
                            onBlur={() => {
                              formik.setFieldTouched("deskripsi", true);
                            }}
                            modules={modules}
                            formats={formats}
                            bounds={'.app'}
                            placeholder="Masukkan deskripsi video..."
                            style={{ height: '200px', marginBottom: '80px' }}
                          />
                          {formik.touched.deskripsi && formik.errors.deskripsi ? (
                            <div className="text-danger fs-6 mt-1">{formik.errors.deskripsi}</div>
                          ) : null}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <ModalFooter className="px-4 py-3 justify-content-end">
                  <Button color="light" onClick={toggleModal}>
                    Batal
                  </Button>
                  <Button color="primary" type="submit" disabled={formik.isSubmitting}>
                    {formik.isSubmitting ? "Menyimpan..." : (isEdit ? "Perbarui" : "Simpan")}
                  </Button>
                </ModalFooter>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  );
};

export default VideoList;